import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import {
  Autocomplete,
  FormHelperText,
  IconButton,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';

import axiosInstance from 'src/utils/axios';

import { TCreateStationeryStockCount } from 'src/redux/slices/stationery-stock-count';

import Iconify from 'src/components/iconify';
import { RHFTextField } from 'src/components/hook-form';

import { IStationeryItemListItem } from 'src/types/stationery-item';

interface Props {
  index: number;
  remove: (index: number) => void;
  isLast: boolean;
}

export default function StationeryStockCountNewItemRow({
  index,
  remove,
  isLast,
}: Props) {
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [localItems, setLocalItems] = useState<IStationeryItemListItem[]>([]);

  const {
    control,
    formState: { errors },
  } = useFormContext<TCreateStationeryStockCount>();

  const defaultHelperText: string = errors.items?.[index] ? ' ' : '';

  // A stationeryItem already picked on another line can't be picked again
  // here — the backend rejects the duplicate on submit anyway (a stock
  // count against the same item twice should be one merged line), so it's
  // filtered out of this row's own options up front.
  const allItems = useWatch({ control, name: 'items' });
  const selectedElsewhere = new Set(
    (allItems ?? [])
      .filter((_, i) => i !== index)
      .map((item) => item?.stationeryItem)
      .filter(Boolean)
  );
  const availableItems = localItems.filter(
    (item) => !selectedElsewhere.has(item._id)
  );

  const selectedItemId = useWatch({
    control,
    name: `items.${index}.stationeryItem` as any,
  });
  const addedQuantity = useWatch({
    control,
    name: `items.${index}.addedQuantity` as any,
  });
  const selectedItem = localItems.find((item) => item._id === selectedItemId);

  const currentStockHelperText = selectedItem
    ? `Current: ${selectedItem.quantity} ${selectedItem.unit?.abbreviation ?? ''} → New total: ${
        selectedItem.quantity + (Number(addedQuantity) || 0)
      } ${selectedItem.unit?.abbreviation ?? ''}`
    : defaultHelperText;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchPaginatedStationeryItems = useCallback(
    debounce(async (query) => {
      try {
        const response = await axiosInstance.get(
          `stationery/item?page=&limit=&generalSearch=${query}&itemName=&itemCode=&minQuantity=&maxQuantity=&unit=&alertEnabled=&status=active&sortBy=itemName&sort=asc`
        );

        setLocalItems(response.data.data.items);
      } catch (err) {
        console.warn(err);
      }
    }, 500),
    []
  );

  useEffect(() => {
    fetchPaginatedStationeryItems(itemSearchQuery);

    return fetchPaginatedStationeryItems.cancel;
  }, [itemSearchQuery, fetchPaginatedStationeryItems]);

  return (
    <TableRow>
      <TableCell>
        {index + 1 < 10 ? `0${index + 1}` : index + 1}

        <FormHelperText>{defaultHelperText}</FormHelperText>
      </TableCell>

      <TableCell sx={{ minWidth: 280 }}>
        <Controller
          name={`items.${index}.stationeryItem`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Autocomplete
              {...field}
              fullWidth
              size="small"
              multiple={false}
              options={availableItems}
              isOptionEqualToValue={(
                option: IStationeryItemListItem | string,
                value: IStationeryItemListItem | string
              ) => {
                const optionId = typeof option === 'string' ? option : option?._id;
                const valueId = typeof value === 'string' ? value : value?._id;
                return optionId === valueId;
              }}
              getOptionLabel={(option: IStationeryItemListItem | string) => {
                if (!option) return '';
                if (typeof option === 'string') {
                  if (option.length === 0) return option;
                  const value = localItems.find(
                    (item: IStationeryItemListItem) => item._id === option
                  );
                  return value ? value.itemName : '';
                }
                return option.itemName;
              }}
              renderOption={(props, option: IStationeryItemListItem | string) => {
                if (typeof option === 'string') {
                  return (
                    <li {...props} key={option}>
                      {option}
                    </li>
                  );
                }
                return (
                  <li {...props} key={option._id}>
                    {option.itemName} ({option.itemCode}) — {option.quantity}{' '}
                    {option.unit?.abbreviation}
                  </li>
                );
              }}
              onInputChange={(_, value, reason) => {
                if (reason === 'input') setItemSearchQuery(value);
                if (reason === 'clear') setItemSearchQuery('');
              }}
              renderInput={(params) => (
                <TextField
                  error={!!error}
                  required
                  helperText={error ? error.message : defaultHelperText}
                  {...params}
                />
              )}
              onChange={(event, newValue) => {
                field.onChange(typeof newValue === 'string' ? newValue : newValue?._id ?? '');
              }}
            />
          )}
        />
      </TableCell>

      <TableCell sx={{ minWidth: 260 }}>
        <RHFTextField
          fullWidth
          required
          type="number"
          size="small"
          name={`items.${index}.addedQuantity`}
          label="Quantity Counted (to add)"
          helperText={currentStockHelperText}
          showError={false}
        />
      </TableCell>

      <TableCell align="right">
        <Tooltip title={isLast ? 'Last item cannot be deleted' : 'Delete'}>
          <span>
            <IconButton disabled={isLast} color="error" onClick={() => remove(index)}>
              <Iconify icon="solar:trash-bin-trash-bold" />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}
